import { useCallback, useEffect, useRef, useState } from 'react';
export function isAutoplayBlockedError(error) {
    return (typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'NotAllowedError');
}
export function useLivePlayback(video, startMuted = true) {
    const [muted, setMutedState] = useState(startMuted);
    const [volume, setVolumeState] = useState(1);
    const lastAudibleRef = useRef(1);
    const userMutedRef = useRef(startMuted);
    const pendingAudibleRef = useRef(false);
    const videoRef = useRef(video);
    videoRef.current = video;
    useEffect(() => {
        if (!video) {
            return;
        }
        let cancelled = false;
        const applyAndPlay = async () => {
            video.volume = volume;
            video.muted = muted;
            try {
                await video.play();
                if (!muted) {
                    pendingAudibleRef.current = false;
                }
            }
            catch (error) {
                if (cancelled) {
                    return;
                }
                if (!muted && isAutoplayBlockedError(error)) {
                    pendingAudibleRef.current = true;
                    video.muted = true;
                    await video.play().catch(() => undefined);
                    setMutedState(true);
                    return;
                }
                await video.play().catch(() => undefined);
            }
        };
        void applyAndPlay();
        const unlockAudible = () => {
            if (cancelled || !pendingAudibleRef.current || userMutedRef.current) {
                return;
            }
            pendingAudibleRef.current = false;
            userMutedRef.current = false;
            video.muted = false;
            void video.play().catch(() => undefined);
            setMutedState(false);
        };
        window.addEventListener('pointerdown', unlockAudible);
        window.addEventListener('keydown', unlockAudible);
        return () => {
            cancelled = true;
            window.removeEventListener('pointerdown', unlockAudible);
            window.removeEventListener('keydown', unlockAudible);
        };
    }, [video, muted, volume]);
    const setVolume = useCallback((next) => {
        const clamped = Math.min(1, Math.max(0, next));
        setVolumeState(clamped);
        if (clamped > 0) {
            lastAudibleRef.current = clamped;
            userMutedRef.current = false;
            pendingAudibleRef.current = false;
            const el = videoRef.current;
            if (el) {
                el.muted = false;
                el.volume = clamped;
                void el.play().catch(() => undefined);
            }
            setMutedState(false);
        }
        else {
            userMutedRef.current = true;
            pendingAudibleRef.current = false;
            const el = videoRef.current;
            if (el) {
                el.muted = true;
                el.volume = 0;
            }
            setMutedState(true);
        }
    }, []);
    const setMuted = useCallback((next) => {
        if (next) {
            userMutedRef.current = true;
            pendingAudibleRef.current = false;
            const el = videoRef.current;
            if (el) {
                el.muted = true;
            }
            setMutedState(true);
            return;
        }
        userMutedRef.current = false;
        const el = videoRef.current;
        if (!el) {
            setMutedState(false);
            return;
        }
        el.muted = false;
        if (volume === 0) {
            const restored = lastAudibleRef.current > 0 ? lastAudibleRef.current : 1;
            el.volume = restored;
            setVolumeState(restored);
        }
        void el.play().then(() => {
            pendingAudibleRef.current = false;
            setMutedState(false);
        }, (error) => {
            if (isAutoplayBlockedError(error)) {
                pendingAudibleRef.current = true;
                el.muted = true;
                void el.play().catch(() => undefined);
                setMutedState(true);
                return;
            }
            pendingAudibleRef.current = false;
            setMutedState(false);
        });
    }, [volume]);
    return { muted, volume, setMuted, setVolume };
}
//# sourceMappingURL=useLivePlayback.js.map